import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function AddUser() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId } = useBranchFilter();
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    role: 'manager'
  });
  const [selectedPages, setSelectedPages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // All available pages in school sidebar
  const availablePages = [
    { id: 'dashboard', name: 'Dashboard' },
    { id: 'students', name: 'Students' },
    { id: 'parents', name: 'Parents' },
    { id: 'teachers', name: 'Teachers' },
    { id: 'receptionists', name: 'Receptionists' },
    { id: 'transport-persons', name: 'Drivers' },
    { id: 'users', name: 'User' },
    { id: 'student-approvals', name: 'Review - Approve / Reject' },
    { id: 'classes', name: 'Classes' },
    { id: 'curriculum-form', name: 'Curriculum' },
    { id: 'class-schedule', name: 'Class Schedule' },
    { id: 'schedule-view', name: 'Schedule View' },
    { id: 'homework', name: 'Homework' },
    { id: 'exam-schedule', name: 'Exam Schedule' },
    { id: 'exam-report-cards', name: 'Exam Report Cards' },
    { id: 'report-cards', name: 'Report Cards' },
    { id: 'attendance-records', name: 'View Attendance' },
    { id: 'set-fees', name: 'Set Fees' },
    { id: 'fee-schedule', name: 'Fee Schedule' },
    { id: 'fee-records', name: 'Fee Records' },
    { id: 'student-fees', name: 'Student Fees' },
    { id: 'notices', name: 'Notices' },
    { id: 'chat', name: 'Chat' },
    { id: 'chat-groups', name: 'Chat Groups' },
    { id: 'admin-chats', name: 'Admin Chats' },
    { id: 'vehicle', name: 'Vehicle' },
    { id: 'routes', name: 'Routes' },
    { id: 'pickup-locations', name: 'Pickup Locations' },
    { id: 'bus-seat-management', name: 'Bus Seat Management' },
    { id: 'meals', name: 'Meals' },
    { id: 'bi-reports', name: 'BI & Reports' },
    { id: 'settings', name: 'Settings' },
    { id: 'timing', name: 'School Timings' },
    { id: 'schedule', name: 'Holiday Selection' },
    { id: 'annual-calendar-form', name: 'Academic Year' },
    { id: 'policies', name: 'Policies / Handbook' },
    { id: 'contact-us', name: 'Contact Us' },
    { id: 'upgrade', name: 'Upgrade' },
    { id: 'my-account', name: 'My Account' }
  ];

  useEffect(() => {
    const sd = localStorage.getItem('schoolData');
    if (!sd) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(sd));
    setLoading(false);
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const togglePage = (pageId) => {
    setSelectedPages(prev => {
      if (prev.includes(pageId)) {
        return prev.filter(id => id !== pageId);
      } else {
        return [...prev, pageId];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validation
    if (!formData.username.trim()) {
      setMessage({ type: 'error', text: 'Username is required' });
      return;
    }
    if (!formData.email.trim()) {
      setMessage({ type: 'error', text: 'Email is required' });
      return;
    }
    if (!formData.password) {
      setMessage({ type: 'error', text: 'Password is required' });
      return;
    }
    if (formData.password.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }
    if (selectedPages.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one page permission' });
      return;
    }

    try {
      setSaving(true);

      // Check if username already exists for this school
      const { data: existingUser } = await supabase
        .from('user_roles')
        .select('id')
        .eq('username', formData.username)
        .eq('school_id', schoolData.id)
        .maybeSingle();

      if (existingUser) {
        setMessage({ type: 'error', text: 'Username already exists in this school' });
        return;
      }

      // Save user directly to user_roles table (no Supabase Auth)
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert({
          username: formData.username,
          email: formData.email,
          password: formData.password, // Store password directly
          full_name: formData.fullName || null,
          role: formData.role,
          page_permissions: selectedPages,
          is_active: true,
          school_id: schoolData.id,
          branch_id: selectedBranchId || null
          // Note: user_id is not included - it's nullable in the table
        });

      if (roleError) throw roleError;

      setMessage({ type: 'success', text: 'User created successfully!' });
      
      // Reset form
      setFormData({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        fullName: '',
        role: 'manager'
      });
      setSelectedPages([]);
    } catch (error) {
      console.error('Error creating user:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to create user. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => navigate('/schoollogin');

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      <div className="main-content" style={{ padding: '2rem', paddingLeft: '2.5rem', paddingRight: '2.5rem' }}>
        <div className="content-header" style={{ marginBottom: '2rem' }}>
          <h1>Add User</h1>
          <p>Create a new user with role and page permissions</p>
        </div>

        {message.text && (
          <div className={`message ${message.type}`} style={{ marginBottom: '1.5rem' }}>
            {message.text}
            <button onClick={() => setMessage({ type: '', text: '' })}>×</button>
          </div>
        )}

        <div style={{ 
          backgroundColor: '#FFFFFF', 
          padding: '2rem', 
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <form onSubmit={handleSubmit}>
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label>Username *</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
                placeholder="Enter username"
                style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid #D1D5DB', borderRadius: '6px' }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Enter email"
                style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid #D1D5DB', borderRadius: '6px' }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label>Full Name</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder="Enter full name"
                style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid #D1D5DB', borderRadius: '6px' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter password (min 6 characters)"
                  minLength={6}
                  style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                />
              </div>

              <div className="form-group">
                <label>Confirm Password *</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  placeholder="Confirm password"
                  style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                />
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label>Role</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid #D1D5DB', borderRadius: '6px' }}
              >
                <option value="head_teacher">Head Teacher</option>
                <option value="manager">Manager</option>
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label>Page Permissions *</label>
              <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '10px' }}>
                Select the pages this user can access:
              </p>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                gap: '10px',
                padding: '15px',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                backgroundColor: '#F9FAFB',
                maxHeight: '400px',
                overflowY: 'auto'
              }}>
                {availablePages.map(page => (
                  <label 
                    key={page.id}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      cursor: 'pointer',
                      padding: '8px',
                      borderRadius: '4px',
                      backgroundColor: selectedPages.includes(page.id) ? '#EFF6FF' : 'white',
                      border: selectedPages.includes(page.id) ? '2px solid #3B82F6' : '1px solid #E5E7EB'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPages.includes(page.id)}
                      onChange={() => togglePage(page.id)}
                      style={{ 
                        margin: 0,
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer',
                        flexShrink: 0
                      }}
                    />
                    <span style={{ 
                      fontSize: '0.875rem',
                      lineHeight: '1.25rem',
                      display: 'flex',
                      alignItems: 'center'
                    }}>{page.name}</span>
                  </label>
                ))}
              </div>
              {selectedPages.length > 0 && (
                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px' }}>
                  Selected: {selectedPages.length} page(s)
                </p>
              )}
            </div>

            <button 
              type="submit" 
              disabled={saving}
              className="btn-primary"
              style={{
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1
              }}
            >
              {saving ? 'Creating User...' : 'Create User'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddUser;
